import { Doc, generateToDocFn } from '@dadohr/shared/domainTypes/document';
import { UserField } from '@dadohr/shared/domainTypes/experience';
import {
  CustomUserField,
  IOrganization,
  isCalculatedFieldKey,
  toCustomFieldKey
} from '@dadohr/shared/domainTypes/org';
import { FieldGetter, Rule } from '@dadohr/shared/domainTypes/rules';
import { Timestamp } from '@dadohr/shared/domainTypes/time';
import {
  CalculatedFieldDefinition,
  CalculatedFieldValues,
  CALCULATED_FIELDS,
  CompositeUserField,
  DefaultDadoField,
  IUser,
  IUserDenormalized,
  StandardUserField
} from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import { compact, escapeRegExp, fromPairs, get, keyBy, toPairs } from 'lodash';
import moment from 'moment-timezone';
import { useCallback, useMemo, useRef } from 'react';
import {
  RuleBuilderConfig,
  RuleBuilderConfigItem
} from '../../features/Experiences/components/RuleBuilder';
import { DEFAULT_DATE_FORMAT } from '../../features/Experiences/components/RuleBuilder/constants';
import {
  UnsafeTFunction,
  useTranslation,
  useUnsafeTranslation
} from '../../i18next';
import { useCurrentUser } from '../currentUser';
import { useFeatureEnabled } from '../features';
import {
  getAllByIdAsMap,
  isFirestoreTimestamp,
  refreshTimestamp,
  store,
  useMappedLoadingValue
} from '../firebase';
import {
  CollectionListener,
  createCollectionListenerStore,
  useCollectionListener
} from '../firecache/collectionListener';
import {
  createDocumentListenerGetter,
  useDocumentListener,
  useDocumentListenersFromStore
} from '../firecache/documentListener';
import { matchRule, matchRuleWithFailures } from '../rules';
import {
  cleanOutEpochDate,
  fromMoment,
  isTimestamp,
  now,
  timestampToMillisSafe,
  toMoment,
  toMomentFromAny,
  tsFromMillis
} from '../time';

export const toUserDoc = generateToDocFn<IUser>((d) => {
  d.personalEmail = (d.personalEmail || '').toLowerCase();
  d.email = (d.email || '').toLowerCase();
  d.customFields = d.customFields || {};
  d.address = d.address || null;
  d.hireDate = cleanOutEpochDate(refreshTimestamp(d.hireDate));
  d.terminationDate = cleanOutEpochDate(refreshTimestamp(d.terminationDate));
  d.dateOfBirth = refreshTimestamp(d.dateOfBirth);
  d.phoneNumber = d.phoneNumber || '';
  Object.entries(d.customFields).forEach(([k, v]) => {
    const normalized = refreshTimestamp(v);
    if (isTimestamp(normalized)) {
      d.customFields[k] = cleanOutEpochDate(normalized);
    }
  });
  return d;
});

const usersByOrgIdListenerStore = createCollectionListenerStore(
  (orgId) =>
    new CollectionListener(
      store().collection(FS.users).where('orgId', '==', orgId),
      toUserDoc
    )
);

// No org in scope (e.g. a rule builder rendered before the org is known):
// resolve to an empty, loaded map rather than firing a live `orgId == ''`
// listener that can never match a user. Lazily built, shared across calls.
let emptyUsersListener: CollectionListener<IUser> | undefined;
const usersByOrgIdStore = (orgId: string): CollectionListener<IUser> => {
  if (orgId) return usersByOrgIdListenerStore(orgId);
  if (!emptyUsersListener) {
    emptyUsersListener = new CollectionListener(
      store().collection(FS.users),
      toUserDoc,
      { disabled: true }
    );
  }
  return emptyUsersListener;
};

export const getUsersByOrgId = async (orgId: string) => {
  return usersByOrgIdStore(orgId).get();
};

export const useUsersByOrgId = (orgId: string) => {
  return useCollectionListener(usersByOrgIdStore(orgId));
};

export const useUsersByOrgIdAsMap = (orgId: string) => {
  return useMappedLoadingValue(
    useCollectionListener(usersByOrgIdStore(orgId)),
    (users) => keyBy(users, (u) => u.id)
  );
};

const userByIdStore = createDocumentListenerGetter(
  (userId) => store().collection(FS.users).doc(userId),
  toUserDoc
);

export const getUser = (userId: string) => {
  return userByIdStore(userId).get();
};

export const getUsers = (userIds: string[]) => {
  return Promise.all(userIds.map(getUser));
};

export const getUsersAsMap = (userIds: string[]) => {
  return getAllByIdAsMap(store().collection(FS.users), userIds, toUserDoc);
};

export const useUserById = (userId: string) => {
  return useDocumentListener(userByIdStore(userId));
};

export const useUsersById = (userIds: string[]) => {
  return useDocumentListenersFromStore(userByIdStore, userIds);
};

// @guide Add a field to the IUser object - 2.4.2 Add example data
export const UNKNOWN_USER = (): Doc<IUser> => ({
  id: 'UNKNOWN',
  collection: FS.users,
  data: {
    alternateId: '',
    firstName: '',
    middleName: '',
    lastName: '',
    preferredName: '',

    gender: 'u',
    email: '',
    personalEmail: '',

    managerId: '',
    orgId: '',

    role: '',
    location: '',
    department: '',
    team: '',
    active: false,

    hireDate: tsFromMillis(946684800000), // Jan 1st 2000
    terminationDate: null,
    dateOfBirth: null,
    address: null,
    phoneNumber: '',
    customFields: {}
  }
});

export const UNKNOWN_USER_2 = (
  orgId: string,
  userId: string,
  t: UnsafeTFunction
): Doc<IUser> => ({
  id: userId,
  collection: FS.users,
  data: {
    alternateId: '',
    firstName: t('Unknown'),
    middleName: '',
    lastName: t('User'),
    preferredName: t('Unknown'),

    gender: 'u',
    email: '',
    personalEmail: '',

    managerId: '',
    orgId,

    role: '',
    location: '',
    department: '',
    team: '',
    active: false,

    hireDate: null,
    terminationDate: null,
    dateOfBirth: null,
    address: null,
    phoneNumber: '',
    customFields: {}
  }
});

export const useUserMapById = (orgId: string, userIds: string[]) => {
  // return useMappedLoadingValue(useUsersById(userIds), (users) => {
  //   return Object.fromEntries(
  //     userIds.map((id, i) => {
  //       const user: Doc<IUser> = users[i] || UNKNOWN_USER();
  //       return [id, user];
  //     })
  //   );
  // });

  const toDict = useCallback(
    (users: Doc<IUser>[]) => {
      const dict = keyBy(users, (u) => u.id);
      return Object.fromEntries(
        userIds.map((id) => {
          const user: Doc<IUser> = dict[id] || UNKNOWN_USER();
          return [id, user];
        })
      );
    },
    [userIds]
  );

  return useMappedLoadingValue(useUsersByOrgId(orgId), toDict, true);
};

const isDateWithinWindow = (
  date: moment.Moment,
  windowSizeInMs: number,
  reference: moment.Moment = moment()
) => {
  const diff = Math.abs(date.diff(reference, 'ms'));
  return diff < windowSizeInMs;
};

export const __getNextRecurringDate = (
  fieldMoment: moment.Moment,
  referenceMoment: moment.Moment,
  interval: { value: number; unit: 'year' | 'month' },
  windowThreshold: number,
  ignoreYear: boolean = false
): { date: moment.Moment; count: number } | null => {
  // Year-agnostic path — used by Next Birthday. Pins month+day to the
  // reference year, then applies the windowThreshold as a days-ago cutoff.
  // A future-dated source year (e.g., customer imports 2025-11-01 into DOB
  // when today is 2025-07-01) yields a negative "days ago", which is always
  // less than the threshold → the computed date is this year's M/D.
  // Mirrors @dadohr/shared/services/utils/calculations.ts; kept locally
  // because the shared services/utils subpath isn't on the published
  // exports map yet (the proper DRY consolidation is tracked separately).
  if (ignoreYear) {
    const thisYearMD = referenceMoment
      .clone()
      .month(fieldMoment.month())
      .date(fieldMoment.date())
      .hour(fieldMoment.hour())
      .minute(fieldMoment.minute())
      .second(fieldMoment.second())
      .millisecond(fieldMoment.millisecond());
    const msSinceThisYearMD = referenceMoment.diff(thisYearMD, 'ms');
    const nextDate =
      msSinceThisYearMD >= windowThreshold
        ? thisYearMD.clone().add(1, 'year')
        : thisYearMD;
    const count = Math.max(0, nextDate.year() - fieldMoment.year());
    return { date: nextDate, count };
  }

  // Year-sensitive path — used by Anniversary. Unchanged: if the source
  // date (hireDate) is in the future, there's no anniversary to show.
  if (fieldMoment.isSameOrAfter(referenceMoment)) {
    return null;
  }
  let count = 0;
  const nextDateInRange = fieldMoment.clone();
  while (nextDateInRange.isBefore(referenceMoment)) {
    count++;
    nextDateInRange.add(interval.value, interval.unit);
    if (
      isDateWithinWindow(nextDateInRange, windowThreshold, referenceMoment) ||
      nextDateInRange.isSameOrAfter(referenceMoment)
    ) {
      return {
        date: nextDateInRange,
        count
      };
    }
  }
  return null;
};

// This is the function which we'll eventually use throughout the app when they become user-defined
// No need to jump the gun here yet - but we're adding it here to test against it right away.
export const __getCalculatedFields = (
  d: IUser,
  referenceDate: Timestamp,
  calculatedFields: CalculatedFieldDefinition[]
) => {
  const n = toMoment(referenceDate);
  const recurringDates = calculatedFields.reduce<
    CalculatedFieldValues['recurringDate']
  >((m, c) => {
    if (c.type !== 'recurringDate') {
      return m;
    }

    // Check if we're in a current window.
    // If yes - take it.
    // Otherwise report the next one
    // If the reference date is later than the actual date report null altogether

    const fieldValue = get(d, c.field);
    const fieldMoment =
      fieldValue && (isFirestoreTimestamp(fieldValue) || timestampToMillisSafe(fieldValue) !== undefined)
        ? toMomentFromAny(fieldValue)
        : null;
    const nextDate = fieldMoment
      ? __getNextRecurringDate(
          fieldMoment,
          n,
          c.interval,
          c.windowThreshold,
          c.ignoreYear ?? false
        )
      : null;
    m[c.field] = {
      current: nextDate
        ? {
            ...nextDate,
            date: fromMoment(nextDate.date)
          }
        : null
    };

    return m;
  }, {});
  return {
    calculatedFields: {
      recurringDate: recurringDates
    }
  };
};

// TODO - this could be optimized a bit.
// Maybe worth caching especially these date calculation
export const getCalculatedFields = (
  d: IUser,
  referenceDate: Timestamp
): { calculatedFields: CalculatedFieldValues } => {
  return __getCalculatedFields(d, referenceDate, CALCULATED_FIELDS);
};

export const fieldGetterPrecise: FieldGetter<UserField, Doc<IUser>> = (
  k,
  t,
  referenceDate
) => {
  const val = isCalculatedFieldKey(k)
    ? get(getCalculatedFields(t.data, referenceDate), k)
    : get(t.data, k);
  const ms = timestampToMillisSafe(val);
  if (ms !== undefined) {
    return ms;
  }
  return val;
};

export const matchUser = (
  user: Doc<IUser>,
  rule: Rule<UserField>,
  referenceDate: Timestamp
) => {
  return matchRule(rule, fieldGetterPrecise, user, referenceDate);
};

// Helper function to get failed fields for a user
export const getUserFailedFields = <Field extends string>(
  user: Doc<IUser>,
  rule: Rule<Field> | null,
  referenceDate: Timestamp
): Set<Field> => {
  if (!rule) return new Set();

  const result = matchRuleWithFailures(
    rule,
    fieldGetterPrecise,
    user,
    referenceDate
  );
  return new Set(result.failedConditions.map((f) => f.field));
};

// @guide Add a field to the IUser object - 2.4.2 Add example data
export const EXAMPLE_USER = (
  orgId: string,
  customFields: CustomUserField[]
): IUser => {
  const u: IUser = {
    alternateId: '',
    firstName: '[First Name]',
    middleName: '',
    lastName: '[Last Name]',
    preferredName: '[First Name]',
    gender: 'f',
    email: 'firstName.lastName@example.com',
    personalEmail: 'personal.email@example.com',
    managerId: '',
    orgId,
    role: '[Role]',
    location: '[Location]',
    department: '[Department]',
    team: '[Team]',
    hireDate: now(),
    terminationDate: null,
    dateOfBirth: null,
    address: null,
    phoneNumber: '',
    active: true,
    customFields: {}
  };

  customFields.forEach((f) => (u.customFields[f.key] = f.label));
  return u;
};

// @guide Add a field to the IUser object - 2.4.1 Add example data
export const EXAMPLE_USER_MAX = (
  orgId: string,
  customFields: CustomUserField[]
): IUser => {
  const u: IUser = {
    alternateId: '',
    firstName: 'Max',
    middleName: '',
    lastName: 'Mayer',
    preferredName: 'Max',
    gender: 'f',
    email: 'max.mayer@example.com',
    personalEmail: 'personal.max@example.com',
    managerId: '',
    orgId,
    role: 'Principal Sofware Engineer',
    location: 'San Francisco, US',
    department: 'Engineering',
    team: 'Web',
    hireDate: now(),
    terminationDate: null,
    dateOfBirth: null,
    address: null,
    phoneNumber: '',
    active: true,
    customFields: {}
  };

  customFields.forEach((f) => (u.customFields[f.key] = f.label));

  return u;
};

// @guide Add a field to the IUser object - 2.3 Add an empty value
export const EMPTY_USER = (orgId: string): IUser => ({
  alternateId: '',
  firstName: '',
  middleName: '',
  lastName: '',
  preferredName: '', // NEED TO TRANSLATE!
  gender: 'u',
  email: '',
  personalEmail: '',
  managerId: '',
  orgId,
  role: '',
  location: '',
  department: '',
  team: '',
  hireDate: now(),
  terminationDate: null,
  dateOfBirth: null,
  address: null,
  phoneNumber: '',
  active: true,
  customFields: {}
});

export const toDenormalizedUser = (u: Doc<IUser>): IUserDenormalized => ({
  publicId: u.id, // in the frontend we have the real id of the user anyway and can just use that
  firstName: u.data.firstName || u.data.preferredName || '',
  lastName: u.data.lastName || '',
  preferredName: u.data.preferredName || u.data.firstName || '',
  email: u.data.email || ''
});

export const toFullName = <
  T extends {
    preferredName: string;
    lastName: string;
  }
>(
  u: T
) => {
  if (!u) {
    return '';
  }
  if (!u.lastName) {
    return u.preferredName || '';
  }
  if (!u.preferredName) {
    return u.lastName || '';
  }
  return `${u.preferredName} ${u.lastName}`;
};

export const toHumanIdentifier = <
  T extends {
    preferredName: string;
    lastName: string;
    email: string;
    personalEmail: string;
  }
>(
  u: T
) => toFullName(u) || u.email || u.personalEmail;

export const toFullyQualifiedName = <
  T extends {
    preferredName: string;
    lastName: string;
    email: string;
  }
>(
  u: T
) => {
  if (u.email) {
    return `${toFullName(u)} <${u.email}>`;
  }
  return toFullName(u);
};

export const toSearchTermRegexp = (q: string) =>
  new RegExp(escapeRegExp(q), 'i');

export const matchUserBySearchTerm = (regexp: RegExp, u: Doc<IUser>) => {
  const { firstName, lastName, preferredName, email, personalEmail } = u.data;
  const candidates = [
    toFullName(u.data),
    firstName && lastName ? `${firstName} ${lastName}` : '',
    email,
    personalEmail
  ];
  return candidates.some(c => c && regexp.test(c));
};

export const getSortedStringSetFromUsers = async (
  orgId: string,
  mapFn: (d: Doc<IUser>) => string
) => {
  const users = await getUsersByOrgId(orgId);
  // compact as some data might be faulty and contain undefined values
  return [...new Set(compact(users.map(mapFn)))].sort();
};

// Rule-builder filter-value suggestions come from the per-org facets projection
// `userFieldFacetsV1/{orgId}` — ONE small doc — instead of loading every org user
// (getSortedStringSetFromUsers is O(all-users) and got slow as orgs grew). The BE
// keeps it in sync (scheduled + import-complete + admin/superadmin/subtask edits).
//
// Falls back to the all-users scan when the doc/field isn't populated yet
// (pre-backfill) so it can never regress. `facetKey` is 'department' | 'role' |
// 'location' | 'team' | `custom.<customFieldKey>` (must match the BE builder's keys).
export const getFacetValuesForField = async (
  orgId: string,
  facetKey: string,
  fallbackMapFn: (d: Doc<IUser>) => string
): Promise<string[]> => {
  try {
    const snap = await store().collection(FS.userFieldFacets).doc(orgId).get();
    const fields = snap.exists
      ? (snap.data() as { fields?: Record<string, string[]> } | undefined)?.fields
      : undefined;
    if (fields && Object.prototype.hasOwnProperty.call(fields, facetKey)) {
      const vals = fields[facetKey];
      return Array.isArray(vals) ? vals : [];
    }
  } catch {
    // fall through to the all-users scan
  }
  return getSortedStringSetFromUsers(orgId, fallbackMapFn);
};

const calculatedFieldToRuleBuilderConfigItem = (
  field: CalculatedFieldDefinition,
  t: UnsafeTFunction
): RuleBuilderConfigItem<UserField> | null => {
  if (field.type === 'recurringDate' && field.exposeCount) {
    return {
      key: `calculatedFields.recurringDate.${field.field}.current.count`,
      label: `${field.label} ${t('Count')}`,
      dataType: 'number'
    };
  }
  return null;
};

export const isUserFieldVisible = (
  context: {
    orgId: string | null;
    experienceTemplateId: string | null;
  },
  fieldLabel: string
) => {
  const { orgId, experienceTemplateId } = context;
  if (orgId === 'OhFDQ3NKE') {
    if (experienceTemplateId === null) {
      return true;
    }

    if (
      experienceTemplateId === '0JiAR8JGo' ||
      experienceTemplateId === 'z0wne9B0j' ||
      experienceTemplateId === '1LA9XP8Ct'
    ) {
      return true;
    }

    const fieldsToHide: string[] = compact([
      'Offboarding - Notes for IT',
      'Offboarding - Term Reason One',
      'Offboarding - Term Reason Two',
      'Offboarding - Severance',
      'Offboarding - Additional Payments Required',
      'Offboarding - COBRA',
      'Offboarding - Notes for Payroll',
      'Offboarding - Regrettable/Not Regrettable'
    ]);
    if (fieldsToHide.includes(fieldLabel)) {
      return false;
    }
  }

  return true;
};

const postFilterUserRuleConfig = (
  config: RuleBuilderConfig<UserField>,
  context: {
    orgId: string | null;
    experienceTemplateId: string | null;
  }
): RuleBuilderConfig<UserField> => {
  return fromPairs(
    compact(
      toPairs(config).map(([k, v]) => {
        if (!isUserFieldVisible(context, v.label)) {
          return null;
        }
        return [k, v];
      })
    )
  );
};

/** Shown in rule text for a user id while the org's user list is still loading. */
export const USER_NAME_LOADING = '…';

/**
 * The org's date format for rule text and date pickers. Every parsed org
 * carries one (`toOrgDoc` fills the default), so the fallback only applies
 * when no org is in scope at all.
 */
export const orgDateFormatOf = (
  org: Pick<IOrganization, 'dateFormats'> | null | undefined
): string => org?.dateFormats?.MMDDYYYY || DEFAULT_DATE_FORMAT;

/** `orgDateFormatOf` for the current user's org; the current user can be absent in a few render paths. */
const useOrgDateFormat = (): string => orgDateFormatOf(useCurrentUser()?.org);

/**
 * Resolves a user id to the display name used in rule text. While the map has
 * not loaded, every id shows an ellipsis. Once loaded, an id with no user (a
 * deleted user) resolves to the unknown-user placeholder rather than an empty
 * string or the raw id.
 *
 * The map is read through a getter on every call, so one formatter instance
 * stays valid (and referentially stable) while the list keeps updating.
 */
export const userNameFormatter =
  (
    orgId: string,
    // `void` because the map comes straight from a LoadingValue tuple.
    getUsersById: () => { [id: string]: Doc<IUser> } | undefined | void,
    t: UnsafeTFunction
  ) =>
  (userId: string): string => {
    const usersById = getUsersById();
    if (!usersById) return USER_NAME_LOADING;
    const u = usersById[userId] ?? UNKNOWN_USER_2(orgId, userId, t);
    return toFullName(u.data);
  };

/**
 * Rule-builder config items for every field that holds a user id: the
 * built-in manager field and each custom field of type `relationship`.
 */
export const relationshipRuleConfigItems = (
  customFields: CustomUserField[],
  orgId: string,
  formatValue: (userId: string) => string,
  t: UnsafeTFunction
): RuleBuilderConfigItem<UserField>[] => [
  {
    key: DefaultDadoField.MANAGER_ID,
    label: t('Manager'),
    dataType: 'user',
    options: { orgId, formatValue }
  },
  ...customFields
    .filter((f) => f.type === 'relationship')
    .map(
      (f): RuleBuilderConfigItem<UserField> => ({
        key: toCustomFieldKey(f.key),
        label: f.label,
        dataType: 'user',
        options: { orgId, formatValue }
      })
    )
];

// @guide Add a field to the IUser object - 4.1 Add to Rule Builder (simple)
//
// This step is optional - if you want to have this field be eligible to right rule against, add it here.
export const useUserRuleConfigSimple = (
  customFields: CustomUserField[],
  tz: string,
  // This is here to do some hacky stuff - hiding specific fields for specific customers
  // and specific orgs
  context: {
    orgId: string | null;
    experienceTemplateId: string | null;
  }
): RuleBuilderConfig<UserField> => {
  const { t } = useTranslation();
  const { t: ut } = useUnsafeTranslation();
  const { orgId, experienceTemplateId } = context;
  const orgDateFormat = useOrgDateFormat();
  // Needed to show names for relationship-field conditions. A null org yields
  // an empty query, so every id then resolves to the unknown-user placeholder.
  const [usersById] = useUsersByOrgIdAsMap(orgId ?? '');
  const usersByIdRef = useRef(usersById);
  usersByIdRef.current = usersById;
  // Rebuilt once when the list arrives, not on every later user change, so
  // the config (and everything memoised on it downstream) stays stable.
  const usersLoaded = !!usersById;
  const formatUser = useMemo(
    () => userNameFormatter(orgId ?? '', () => usersByIdRef.current, ut),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orgId, ut, usersLoaded]
  );

  const config = useMemo(() => {
    const dateOptions = { tz, dateFormat: orgDateFormat };
    const standardFields: {
      [K in UserField]: RuleBuilderConfigItem<UserField>;
    } = {
      role: {
        key: 'role',
        label: t('Role'),
        dataType: 'string'
      },

      location: {
        key: 'location',
        label: t('Location'),
        dataType: 'string'
      },
      department: {
        key: 'department',
        label: t('Department'),
        dataType: 'string'
      },
      team: {
        key: 'team',
        label: t('Team'),
        dataType: 'string'
      },
      hireDate: {
        key: 'hireDate',
        label: t('Start Date'),
        dataType: 'date',
        options: dateOptions
      },
      terminationDate: {
        key: 'terminationDate',
        label: ut('Last day of work'),
        dataType: 'date',
        options: dateOptions
      },

      email: {
        key: DefaultDadoField.EMAIL,
        label: ut('Work Email'),
        dataType: 'string',
        options: {
          allowedOperators: ['isBlank', 'isNotBlank']
        }
      },

      personalEmail: {
        key: DefaultDadoField.PERSONAL_EMAIL,
        label: ut('Personal Email'),
        dataType: 'string',
        options: {
          allowedOperators: ['isBlank', 'isNotBlank']
        }
      },
      alternateId: {
        key: DefaultDadoField.ALTERNATE_ID,
        label: ut('Alternate ID'),
        dataType: 'string',
        options: {
          allowedOperators: ['isBlank', 'isNotBlank']
        }
      }
    };

    CALCULATED_FIELDS.forEach((c) => {
      const item = calculatedFieldToRuleBuilderConfigItem(c, ut);
      if (item) {
        standardFields[item.key] = item;
      }
    });

    customFields.forEach((f) => {
      // Relationship fields are added by relationshipRuleConfigItems below;
      // address fields cannot be used in rules.
      if (f.type !== 'relationship' && f.type !== 'address') {
        const key = toCustomFieldKey(f.key);
        standardFields[key] =
          f.type === 'date' || f.type === 'smartDate'
            ? {
                key,
                label: f.label,
                dataType: 'date' as const,
                options: dateOptions
              }
            : {
                key,
                label: f.label,
                dataType: f.type === 'smartText' ? 'string' : f.type
                // TODO deal with boolean type
              };
      }
    });
    relationshipRuleConfigItems(customFields, orgId ?? '', formatUser, ut).forEach(
      (item) => {
        standardFields[item.key] = item;
      }
    );
    return standardFields;
  }, [t, ut, customFields, tz, orgId, formatUser, orgDateFormat]);

  return useMemo(
    () => postFilterUserRuleConfig(config, { orgId, experienceTemplateId }),
    [config, orgId, experienceTemplateId]
  );
};

const CUSTOM_OPTIONS: {
  [orgId: string]: {
    [field: string]: string[];
  };
} = {
  // mixpanel
  OhFDQ3NKE: {
    location: [
      'San Francisco',
      'New York City',
      'Barcelona',
      'London',
      'Remote, US',
      'Remote, International',
      'Singapore',
      'Austin',
      'Bangalore',
      'Seattle'
    ],
    [toCustomFieldKey('employmentType')]: [
      'Full-time',
      'Part-time',
      '1099 Contractor',
      'Short Term Employment',
      'PEO'
    ],
    [toCustomFieldKey('exemptStatus')]: [
      'Exempt',
      'Non-Exempt',
      'International'
    ]
  }
};

export const tryToAddCustomOptionsForOrg = (
  orgId: string,
  field: string,
  strings: string[]
): string[] => {
  const customOptions = CUSTOM_OPTIONS[orgId]?.[field];
  return customOptions
    ? [...new Set([...strings, ...customOptions])].sort()
    : strings;
};

// @guide Add a field to the IUser object - 4.2 Add to Rule Builder (with autocompletion)
//
// If you did the last step, also add the field here and optionally define autocompletion instructions.
// This will not make sense for all fields, e.g. down below you see that this is provided for e.g. "role",
// but not for "terminationDate"
export const useUserRuleConfigWithAutocompletion = (
  orgId: string,
  customFields: CustomUserField[],
  tz: string,
  context?: {
    orgId: string;
    experienceTemplateId?: string | null;
  }
): RuleBuilderConfig<UserField> => {
  const { t } = useUnsafeTranslation();
  const orgDateFormat = useOrgDateFormat();
  // Needed to show names for relationship-field conditions.
  const [usersById] = useUsersByOrgIdAsMap(orgId);
  const usersByIdRef = useRef(usersById);
  usersByIdRef.current = usersById;
  // Rebuilt once when the list arrives, not on every later user change, so
  // the config (and everything memoised on it downstream) stays stable.
  const usersLoaded = !!usersById;
  const formatUser = useMemo(
    () => userNameFormatter(orgId, () => usersByIdRef.current, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orgId, t, usersLoaded]
  );
  // Gate the rule-builder filter-value SOURCE under PEOPLE_TAB_V2 so the whole
  // read-path bundle rides one flag: read the per-org facets projection when the
  // flag is on, fall back to the legacy all-users scan when off.
  const peopleTabV2 = useFeatureEnabled('PEOPLE_TAB_V2');
  const config = useMemo(() => {
    const dateOptions = { tz, dateFormat: orgDateFormat };
    const getFilterValues = (
      orgIdArg: string,
      facetKey: string,
      fallbackMapFn: (u: Doc<IUser>) => string
    ): Promise<string[]> =>
      peopleTabV2
        ? getFacetValuesForField(orgIdArg, facetKey, fallbackMapFn)
        : getSortedStringSetFromUsers(orgIdArg, fallbackMapFn);
    const standardFields: {
      [K in UserField]: RuleBuilderConfigItem<UserField>;
    } = {
      department: {
        key: 'department',
        label: t('Department'),
        dataType: 'string',
        options: {
          autocomplete: {
            getItems: () =>
              getFilterValues(
                orgId,
                'department',
                (u) => u.data.department
              ).then((set) =>
                tryToAddCustomOptionsForOrg(orgId, 'department', set)
              )
          }
        }
      },
      role: {
        key: 'role',
        label: t('Role'),
        dataType: 'string',
        options: {
          autocomplete: {
            getItems: () =>
              getFilterValues(
                orgId,
                'role',
                (u) => u.data.role
              ).then((set) => tryToAddCustomOptionsForOrg(orgId, 'role', set))
          }
        }
      },

      location: {
        key: 'location',
        label: t('Location'),
        dataType: 'string',
        options: {
          autocomplete: {
            getItems: () =>
              getFilterValues(
                orgId,
                'location',
                (u) => u.data.location
              ).then((set) =>
                tryToAddCustomOptionsForOrg(orgId, 'location', set)
              )
          }
        }
      },
      team: {
        key: 'team',
        label: t('Team'),
        dataType: 'string',
        options: {
          autocomplete: {
            getItems: () =>
              getFilterValues(
                orgId,
                'team',
                (u) => u.data.team
              ).then((set) => tryToAddCustomOptionsForOrg(orgId, 'team', set))
          }
        }
      },
      hireDate: {
        key: 'hireDate',
        label: t('Start Date'),
        dataType: 'date',
        options: dateOptions
      },
      terminationDate: {
        key: 'terminationDate',
        label: t('Last day at work'),
        dataType: 'date',
        options: dateOptions
      },

      email: {
        key: DefaultDadoField.EMAIL,
        label: t('Work Email'),
        dataType: 'string',
        options: {
          allowedOperators: ['isBlank', 'isNotBlank']
        }
      },

      personalEmail: {
        key: DefaultDadoField.PERSONAL_EMAIL,
        label: t('Personal Email'),
        dataType: 'string',
        options: {
          allowedOperators: ['isBlank', 'isNotBlank']
        }
      },
      alternateId: {
        key: DefaultDadoField.ALTERNATE_ID,
        label: t('Alternate ID'),
        dataType: 'string',
        options: {
          allowedOperators: ['isBlank', 'isNotBlank']
        }
      }
    };

    CALCULATED_FIELDS.forEach((c) => {
      const item = calculatedFieldToRuleBuilderConfigItem(c, t);
      if (item) {
        standardFields[item.key] = item;
      }
    });

    customFields.forEach((f) => {
      // if (f.restricted) {
      //   return;
      // }
      // Relationship fields are added by relationshipRuleConfigItems below;
      // address fields cannot be used in rules.
      if (f.type !== 'relationship' && f.type !== 'address') {
        const key = toCustomFieldKey(f.key);
        standardFields[key] =
          f.type === 'date' || f.type === 'smartDate'
            ? {
                key,
                label: f.label,
                dataType: 'date' as const,
                options: dateOptions
              }
            : {
                key,
                label: f.label,
                dataType: f.type === 'smartText' ? 'string' : f.type
                // TODO deal with boolean type
              };
        if (f.type === 'string' || f.type === 'smartText') {
          standardFields[key].options = {
            autocomplete: {
              getItems: () =>
                getFilterValues(orgId, `custom.${f.key}`, (u) =>
                  String(get(u.data, key) ?? '')
                ).then((set) => tryToAddCustomOptionsForOrg(orgId, key, set))
            }
          };
        }
      }
    });
    relationshipRuleConfigItems(customFields, orgId, formatUser, t).forEach(
      (item) => {
        standardFields[item.key] = item;
      }
    );
    return standardFields;
  }, [orgId, customFields, t, tz, peopleTabV2, formatUser, orgDateFormat]);

  const finalContext = context || { orgId };
  const experienceTemplateId = finalContext.experienceTemplateId;

  return useMemo(
    () =>
      postFilterUserRuleConfig(config, {
        orgId,
        experienceTemplateId: experienceTemplateId ?? null
      }),
    [config, orgId, experienceTemplateId]
  );
};

export const compositeUserFields = (fields: string[], t: UnsafeTFunction) => {
  const allFields: CompositeUserField[] = [
    {
      key: '$FULL_NAME$',
      type: 'string',
      label: t('Full Name'),
      description: '',
      restricted: false
    },
    {
      key: '$FULL_NAME_LN_FN$',
      type: 'string',
      label: t('Full Name (Last Name, First Name)'),
      description: '',
      restricted: false
    }
  ];
  return allFields.filter((f) => fields.includes(f.key));
};

// @guide Add a field to the IUser object - 3.2 Define as StandardUserField data
//
// This is the actual list that is used the render the UI elements mentioned in the step before
// The key is also translated. Go to the translation file and look for `userField` - add your
// new field there.
export const standardUserFields = (t: UnsafeTFunction): StandardUserField[] => {
  const fields: StandardUserField[] = [
    {
      key: 'firstName',
      type: 'string',
      label: '',
      description: '',
      restricted: false
    },
    {
      key: 'preferredName',
      type: 'string',
      label: '',
      description: '',
      restricted: false
    },
    {
      key: 'middleName',
      type: 'string',
      label: '',
      description: '',
      restricted: false
    },
    {
      key: 'lastName',
      type: 'string',
      label: '',
      description: '',
      restricted: false
    },
    {
      key: 'gender',
      type: 'string',
      label: '',
      description: '',
      restricted: false
    },

    {
      key: 'alternateId',
      type: 'string',
      label: '',
      description: '',
      restricted: false
    },
    {
      key: 'email',
      type: 'string',
      label: '',
      description: '',
      restricted: false
    },
    {
      key: 'personalEmail',
      type: 'string',
      label: '',
      description: '',
      restricted: false
    },
    {
      key: 'managerId',
      type: 'relationship',
      label: '',
      description: '',
      identifyBy: 'email',
      restricted: false
    },

    {
      key: 'location',
      type: 'string',
      label: '',
      description: '',
      restricted: false
    },
    {
      key: 'role',
      type: 'string',
      label: '',
      description: '',
      restricted: false
    },
    {
      key: 'department',
      type: 'string',
      label: '',
      description: '',
      restricted: false
    },
    {
      key: 'team',
      type: 'string',
      label: '',
      description: '',
      restricted: false
    },
    {
      key: 'address',
      type: 'address',
      label: '',
      description: '',
      restricted: false
    },
    {
      key: 'hireDate',
      type: 'date',
      label: '',
      description: '',
      restricted: false,
      hideYear: false
    },
    {
      key: 'terminationDate',
      type: 'date',
      label: '',
      description: '',
      restricted: false,
      hideYear: false
    },
    {
      key: 'dateOfBirth',
      type: 'date',
      label: '',
      description: '',
      restricted: false,
      hideYear: true
    },
    {
      key: 'phoneNumber',
      type: 'string',
      label: '',
      description: '',
      restricted: false
    },
    {
      key: 'active',
      type: 'boolean',
      label: '',
      description: '',
      restricted: false
    }
  ];
  // TODO - address is not allowed yet, as this needs to expand to a more complex mapping
  return fields.map((d) => {
    d.label = t(`userField.${d.key}`);
    return d;
  });
};

const getUserFields = (
  customFields: CustomUserField[],
  t: UnsafeTFunction,
  includeRestricted = false
): (StandardUserField | CustomUserField)[] => {
  return [
    ...standardUserFields(t),
    ...customFields
      .filter((f) => f.type !== 'smartDate')
      .map((f) => ({ ...f, key: toCustomFieldKey(f.key) }))
  ].filter((f) => !f.restricted || includeRestricted);
};

export const useUserFields = (
  customFields: CustomUserField[],
  includeRestricted = false
): (StandardUserField | CustomUserField)[] => {
  const { t } = useUnsafeTranslation();
  return useMemo(() => getUserFields(customFields, t, includeRestricted), [
    t,
    customFields,
    includeRestricted
  ]);
};

export const useUserFieldLableMap = (
  includeRestricted = false
): { [key: string]: string } => {
  const { org } = useCurrentUser();
  const userFields = useUserFields(org.customUserFields, includeRestricted);
  return useMemo(() => {
    return userFields.reduce((map, field) => {
      map[field.key] = field.label;
      return map;
    }, {} as { [key: string]: string });
  }, [userFields]);
};

export const getUserValue = (
  user: IUser,
  field: string,
  referenceDate: Timestamp
) => {
  const data = isCalculatedFieldKey(field)
    ? getCalculatedFields(user, referenceDate)
    : user;

  return get(data, field);
};

// toEmployeeAppDashboardUrl / useEmployeeAppDashboardUrlForUserId /
// getUserAcessTokenForUserId moved to ./employeeAppDashboardUrl.ts (2026-08)
// — they used `import.meta.env.DEV`, which crashes this app's Jest/@swc-jest
// transform the moment anything imports this file, including totally
// unrelated exports. See that file's header comment.
